import { useMemo, useState } from "react";
import { Copy, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { copyText } from "@/lib/gente-gestao/format";
import {
  csvCicloConvitesUrl,
  downloadGgCsv,
  gerarConvitesLiderancaApi,
  marcarEnviadoLiderancaApi,
  type GgCicloDetalheDto,
  type GgConviteLiderancaDto,
  type GgParLiderancaDto,
} from "@/services/genteGestao";

function interpolar(
  template: string,
  vars: { nome: string; link: string; empresa: string; prazo: string; lider: string },
): string {
  return template
    .replace(/\{nome\}/g, vars.nome)
    .replace(/\{link\}/g, vars.link)
    .replace(/\{empresa\}/g, vars.empresa)
    .replace(/\{prazo\}/g, vars.prazo)
    .replace(/\{lider\}/g, vars.lider);
}

function prazoLabel(dataFim: string | null): string {
  if (!dataFim) return "sem prazo definido";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dataFim);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dataFim;
}

function statusEnvio(c: { abertoEm: string | null; enviadoEm: string | null }): "Aberto" | "Enviado" | "Não enviado" {
  if (c.abertoEm) return "Aberto";
  if (c.enviadoEm) return "Enviado";
  return "Não enviado";
}

function Badge({
  label,
  tone,
}: {
  label: string;
  tone: "emerald" | "amber" | "slate";
}) {
  const cls =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-800"
      : tone === "amber"
        ? "bg-amber-50 text-amber-800"
        : "bg-slate-100 text-slate-600";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

export function AbaLiderancaParticipantes({
  ciclo,
  convites,
  pares,
  onChanged,
}: {
  ciclo: GgCicloDetalheDto;
  convites: GgConviteLiderancaDto[];
  pares: GgParLiderancaDto[];
  onChanged: () => void;
}) {
  const [setor, setSetor] = useState("");
  const [liderId, setLiderId] = useState("");
  const [mensagem, setMensagem] = useState(ciclo.mensagemConvite ?? "");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const setores = useMemo(() => {
    const s = new Set<string>();
    for (const p of pares) if (p.setor) s.add(p.setor);
    return [...s].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [pares]);

  const lideres = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of pares) map.set(p.liderId, p.liderNome);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "pt-BR"));
  }, [pares]);

  function flash(text: string) {
    setFeedback(text);
    window.setTimeout(() => setFeedback(null), 2200);
  }

  async function gerar(opts: { todosAtivos?: boolean; setor?: string | null; liderIds?: string[] }) {
    setBusy(true);
    try {
      const n = await gerarConvitesLiderancaApi(ciclo.id, opts);
      flash(n === 0 ? "Nenhum convite novo (já existiam ou não há pares)." : `${n} convite(s) gerado(s).`);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  function mensagemDe(c: GgConviteLiderancaDto): string {
    return interpolar(mensagem || "", {
      nome: c.nome,
      lider: c.liderNome,
      link: c.link,
      empresa: ciclo.nomeEmpresa,
      prazo: prazoLabel(ciclo.dataFim),
    });
  }

  async function copiar(text: string, ok = "Copiado!") {
    const done = await copyText(text);
    flash(done ? ok : "Não foi possível copiar.");
  }

  const pendentes = convites.filter((c) => !c.respondidoEm);

  return (
    <div className="flex flex-col gap-4">
      {feedback && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{feedback}</p>
      )}
      {ciclo.status !== "ATIVA" && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Os links só funcionam depois de clicar em <strong>Ativar ciclo</strong>.
        </p>
      )}
      {pares.length === 0 && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Cadastre o líder direto nos colaboradores para gerar os pares avaliador–líder.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy || pares.length === 0} onClick={() => void gerar({ todosAtivos: true })}>
          Gerar todos os pares
        </Button>
        <div className="flex items-center gap-2">
          <Select
            value={setor}
            onChange={(e) => setSetor(e.target.value)}
            options={setores.map((s) => ({ value: s, label: s }))}
            placeholder="Setor"
          />
          <Button size="sm" variant="secondary" disabled={!setor || busy} onClick={() => void gerar({ setor })}>
            Por setor
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={liderId}
            onChange={(e) => setLiderId(e.target.value)}
            options={lideres.map(([id, nome]) => ({ value: id, label: nome }))}
            placeholder="Líder"
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={!liderId || busy}
            onClick={() => void gerar({ liderIds: [liderId], todosAtivos: true })}
          >
            Por líder
          </Button>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mensagem do WhatsApp</label>
        <textarea
          className="mt-1.5 min-h-[80px] w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-400">
          Variáveis: {"{nome}"} {"{lider}"} {"{link}"} {"{empresa}"} {"{prazo}"}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void copiar(convites.map((c) => `${c.nome} → ${c.liderNome} – ${c.link}`).join("\n"), "Lista copiada.")}
        >
          Copiar lista de links
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void downloadGgCsv(csvCicloConvitesUrl(ciclo.id), `convites-${ciclo.slug}.csv`)}
        >
          Exportar CSV
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void copiar(pendentes.map((c) => `${c.nome} → ${c.liderNome} – ${c.link}`).join("\n"), "Pendentes copiados.")}
        >
          Copiar pendentes
        </Button>
      </div>

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
        {convites.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-500">Nenhum convite ainda.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Avaliador</th>
                <th className="px-3 py-2">Líder</th>
                <th className="px-3 py-2">Setor</th>
                <th className="px-3 py-2">Envio</th>
                <th className="px-3 py-2">Resposta</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {convites.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{c.nome}</td>
                  <td className="px-3 py-2">{c.liderNome}</td>
                  <td className="px-3 py-2">{c.setor ?? "—"}</td>
                  <td className="px-3 py-2">
                    <Badge
                      label={statusEnvio(c)}
                      tone={c.enviadoEm || c.abertoEm ? "slate" : "amber"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      label={c.respondidoEm ? "Respondido" : "Pendente"}
                      tone={c.respondidoEm ? "emerald" : "amber"}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button size="sm" variant="ghost" leftIcon={<Copy className="h-4 w-4" />} onClick={() => void copiar(c.link)}>
                        Copiar
                      </Button>
                      {c.telefone && (
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<MessageCircle className="h-4 w-4" />}
                          onClick={() => {
                            const url = `https://wa.me/${c.telefone}?text=${encodeURIComponent(mensagemDe(c))}`;
                            window.open(url, "_blank", "noopener,noreferrer");
                            void marcarEnviadoLiderancaApi(ciclo.id, c.id).then(onChanged);
                          }}
                        >
                          WhatsApp
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
